import type { CSSProperties, ReactNode } from "react";

export function Card({
	children,
	className = "",
	style,
}: {
	children: ReactNode;
	className?: string;
	style?: CSSProperties;
}) {
	return (
		<div
			className={`rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] shadow-sm p-5 ${className}`}
			style={style}
		>
			{children}
		</div>
	);
}

export function Skeleton({
	width,
	height = "1rem",
	radius = "var(--radius-md)",
	style,
}: {
	width?: number | string;
	height?: number | string;
	radius?: number | string;
	style?: CSSProperties;
}) {
	return (
		<div
			aria-hidden
			className="skeleton"
			style={{
				width: width ?? "100%",
				height,
				borderRadius: radius,
				...style,
			}}
		/>
	);
}

export function SkeletonCard({
	rows = 3,
	style,
}: {
	rows?: number;
	style?: CSSProperties;
}) {
	return (
		<Card style={{ display: "grid", gap: "0.6rem", ...style }}>
			<Skeleton width="40%" height="1.1rem" />
			{Array.from({ length: rows }).map((_, i) => (
				<Skeleton
					key={i}
					width={i === rows - 1 ? "60%" : "100%"}
					height="0.85rem"
				/>
			))}
		</Card>
	);
}

export function PageSkeleton({
	variant = "default",
}: {
	variant?: "default" | "result" | "list" | "quiz" | "streak" | "profile" | "kenali";
}) {
	if (variant === "result") {
		return (
			<div style={{ display: "grid", gap: "0.75rem" }}>
				<Card
					style={{
						display: "grid",
						justifyItems: "center",
						gap: "0.6rem",
						padding: "1.5rem 1rem",
					}}
				>
					<Skeleton width={160} height={80} radius="50%" />
					<Skeleton width="50%" height="1.2rem" />
					<Skeleton width="35%" height="0.85rem" />
				</Card>
				<SkeletonCard rows={3} />
				<SkeletonCard rows={3} />
			</div>
		);
	}
	if (variant === "list") {
		return (
			<div style={{ display: "grid", gap: "0.5rem" }}>
				<Skeleton width="40%" height="1.1rem" style={{ marginBottom: "0.4rem" }} />
				<SkeletonCard rows={2} />
				<SkeletonCard rows={2} />
				<SkeletonCard rows={2} />
			</div>
		);
	}
	if (variant === "quiz") {
		return (
			<div style={{ display: "grid", gap: "0.75rem" }}>
				<Skeleton width="20%" height="0.8rem" />
				<Skeleton width="85%" height="1.2rem" />
				<div style={{ display: "grid", gap: "0.5rem" }}>
					<Skeleton height="3rem" />
					<Skeleton height="3rem" />
					<Skeleton height="3rem" />
					<Skeleton height="3rem" />
				</div>
			</div>
		);
	}
	if (variant === "streak") {
		return (
			<div style={{ display: "grid", gap: "0.75rem" }}>
				<Card
					style={{
						display: "grid",
						justifyItems: "center",
						gap: "0.5rem",
						padding: "1.25rem 1rem",
					}}
				>
					<Skeleton width={120} height={120} radius="50%" />
					<Skeleton width="45%" height="1.2rem" />
					<Skeleton width="35%" height="0.85rem" />
				</Card>
				<Card style={{ display: "grid", gap: "0.85rem" }}>
					<Skeleton width="35%" height="1rem" />
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							gap: "0.4rem",
						}}
					>
						{Array.from({ length: 7 }).map((_, i) => (
							<Skeleton key={i} height={36} />
						))}
					</div>
				</Card>
			</div>
		);
	}
	if (variant === "profile") {
		return (
			<div style={{ display: "grid", gap: "0.75rem" }}>
				<Skeleton width="30%" height="1.4rem" />
				<Card
					style={{
						display: "grid",
						justifyItems: "center",
						gap: "0.5rem",
						padding: "1rem",
					}}
				>
					<Skeleton width={96} height={96} radius="50%" />
					<Skeleton width="55%" height="1rem" />
					<Skeleton width="40%" height="0.85rem" />
				</Card>
				<Card style={{ display: "grid", gap: "0.6rem" }}>
					<Skeleton width="100%" height="2.5rem" />
					<Skeleton width="100%" height="2.5rem" />
				</Card>
			</div>
		);
	}
	if (variant === "kenali") {
		return (
			<div style={{ display: "grid", gap: "0.75rem" }}>
				<Card style={{ display: "grid", gap: "0.5rem" }}>
					<Skeleton width="45%" height="1.2rem" />
					<Skeleton width="80%" height="0.85rem" />
				</Card>
				<SkeletonCard rows={4} />
				<SkeletonCard rows={2} />
			</div>
		);
	}
	return (
		<div style={{ display: "grid", gap: "0.75rem" }}>
			<Skeleton width="35%" height="1.4rem" />
			<SkeletonCard rows={2} />
			<SkeletonCard rows={3} />
		</div>
	);
}

export function EmptyState({
	icon,
	title,
	description,
	action,
	noIconBg,
}: {
	icon: ReactNode;
	title: string;
	description: string;
	action?: ReactNode;
	noIconBg?: boolean;
}) {
	return (
		<Card>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: "0.75rem",
					padding: "1.5rem 0.5rem",
					textAlign: "center",
				}}
			>
				{noIconBg ? (
					icon
				) : (
					<div
						style={{
							width: 64,
							height: 64,
							borderRadius: "var(--radius-full)",
							background: "var(--color-primary-soft)",
							color: "var(--color-primary)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{icon}
					</div>
				)}
				<h3 style={{ margin: 0 }}>{title}</h3>
				<p
					className="muted"
					style={{ margin: 0, maxWidth: 360, lineHeight: 1.5 }}
				>
					{description}
				</p>
				{action}
			</div>
		</Card>
	);
}

export function WaitingState({
	icon,
	title,
	description,
}: {
	icon?: ReactNode;
	title: string;
	description: string;
}) {
	return (
		<Card>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.85rem",
				}}
			>
				{icon ?? <Pill tone="warning">⏳</Pill>}
				<div style={{ flex: 1 }}>
					<h3 style={{ margin: 0 }}>{title}</h3>
					<p className="muted" style={{ margin: "0.25rem 0 0" }}>
						{description}
					</p>
				</div>
				<div style={{ display: "flex", gap: 6 }}>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: 8,
							background: "var(--color-primary)",
							animation: "pulse-dot 1.2s ease-in-out infinite",
						}}
					/>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: 8,
							background: "var(--color-primary)",
							animation: "pulse-dot 1.2s ease-in-out infinite",
							animationDelay: "0.2s",
						}}
					/>
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: 8,
							background: "var(--color-primary)",
							animation: "pulse-dot 1.2s ease-in-out infinite",
							animationDelay: "0.4s",
						}}
					/>
				</div>
			</div>
		</Card>
	);
}

export function Label({
	children,
	htmlFor,
}: {
	children: ReactNode;
	htmlFor?: string;
}) {
	return (
		<label
			htmlFor={htmlFor}
			className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
		>
			{children}
		</label>
	);
}

export function Input({
	className = "",
	...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
	return (
		<input
			className={`w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-input-bg)] px-3 py-2 text-sm outline-none focus:border-[color:var(--color-primary)] ${className}`}
			{...props}
		/>
	);
}

export function ErrorBlock({ message }: { message: string }) {
	return (
		<div
			className="rounded-md px-4 py-3 font-medium"
			style={{
				background: "var(--color-error-soft)",
				color: "var(--color-error)",
			}}
		>
			{message}
		</div>
	);
}

export function InfoBlock({
	children,
	tone = "info",
}: {
	children: ReactNode;
	tone?: "info" | "warning" | "success";
}) {
	const colors = {
		info: { bg: "var(--color-info-soft)", fg: "var(--color-info)" },
		warning: { bg: "var(--color-warning-soft)", fg: "var(--color-warning)" },
		success: { bg: "var(--color-success-soft)", fg: "var(--color-success)" },
	}[tone];
	return (
		<div
			className="rounded-md px-4 py-3 text-sm leading-relaxed"
			style={{ background: colors.bg, color: colors.fg }}
		>
			{children}
		</div>
	);
}

export function Pill({
	children,
	tone = "default",
}: {
	children: ReactNode;
	tone?: "default" | "warning" | "success" | "info";
}) {
	const map: Record<string, { bg: string; fg: string }> = {
		default: {
			bg: "var(--color-primary-soft)",
			fg: "var(--color-primary)",
		},
		warning: {
			bg: "var(--color-warning-soft)",
			fg: "var(--color-warning)",
		},
		success: {
			bg: "var(--color-success-soft)",
			fg: "var(--color-success)",
		},
		info: { bg: "var(--color-info-soft)", fg: "var(--color-info)" },
	};
	const c = map[tone] ?? map.default;
	return (
		<span
			className="inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-semibold"
			style={{ background: c.bg, color: c.fg }}
		>
			{children}
		</span>
	);
}

export function Button({
	children,
	variant = "primary",
	className = "",
	style: propStyle,
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
	variant?: "primary" | "secondary" | "ghost";
}) {
	const base =
		"inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed";
	const styles: Record<string, React.CSSProperties> = {
		primary: {
			background: "var(--color-primary)",
			color: "#fff",
			boxShadow: "var(--shadow-sm)",
		},
		secondary: {
			background: "var(--color-surface)",
			color: "var(--color-primary)",
			border: "1px solid var(--color-border-strong)",
		},
		ghost: {
			background: "transparent",
			color: "var(--color-text)",
		},
	};
	const baseStyle = styles[variant] ?? styles.primary;
	return (
		<button
			className={`${base} ${className}`}
			style={{ ...baseStyle, ...propStyle }}
			{...props}
		>
			{children}
		</button>
	);
}
